/* Buzzwoo · Ideenverlag · Popup mit Slides
 *
 * Winziges Custom Element fuer die Slide-Navigation im Popup. Wir nutzen
 * bewusst NICHT Ignites <slider-component>:
 *
 *   ModalDialog.moveModal() (global.js:886-903) haengt das Modal beim ersten
 *   Oeffnen per document.body.appendChild() um. SliderComponent hat ein
 *   disconnectedCallback (global.js:1750-1797), das alle Listener abraeumt,
 *   aber KEIN connectedCallback — init() laeuft einmalig aus dem Constructor.
 *   Ein slider-component, das den Umzug erlebt, ist danach tot.
 *   (Das Verschieben zu unterdruecken waere die schlechtere Wahl: in der
 *   Section haengt das Popup unter jedem .isolate-Vorfahren, base.css:164,
 *   und laege damit unter dem Sticky-Header.)
 *
 * Ausserdem gibt es in SliderComponent weder Punkte noch Autoplay — beides
 * steckt in SlideshowComponent und ist an dessen Markup gebunden.
 *
 * Vorbild ist bw-iv-themenwelt-vergleich.js (gleiche Problemklasse, laeuft
 * nachweislich in einem verschobenen Modal). Wichtige Ergaenzung: dieses
 * Element ist IDEMPOTENT. Der Umzug loest ein zweites connectedCallback aus;
 * ohne teardown() haetten wir doppelte Listener — und spaeter einen zweiten
 * Autoplay-Timer, also doppeltes Tempo.
 *
 * Wischen kommt vollstaendig aus CSS (scroll-snap), kein Touch-Handling hier.
 */
if (!customElements.get('bw-iv-popup-slides')) {
  class BwIvPopupSlides extends HTMLElement {
    connectedCallback() {
      // Immer zuerst aufraeumen: beim Umzug nach <body> laeuft diese Methode
      // ein zweites Mal auf demselben Element.
      this.teardown();

      this.rail = this.querySelector('.bw-iv-pops__rail');
      if (!this.rail) return;

      this.slides = Array.from(this.rail.querySelectorAll('.bw-iv-pops__slide'));
      if (this.slides.length < 2) return;

      this.dots = Array.from(this.querySelectorAll('.bw-iv-pops__dot'));
      this.prev = this.querySelector('.bw-iv-pops__arrow--prev');
      this.next = this.querySelector('.bw-iv-pops__arrow--next');
      this.index = 0;
      this.cleanups = [];

      this.syncState();
      this.bindObserver();
      this.bindControls();
      this.bindAutoplay();
    }

    disconnectedCallback() {
      this.teardown();
    }

    teardown() {
      if (this.cleanups) this.cleanups.forEach((fn) => fn());
      this.cleanups = [];
    }

    on(target, type, handler) {
      target.addEventListener(type, handler);
      this.cleanups.push(() => target.removeEventListener(type, handler));
    }

    bindObserver() {
      // Der Beobachter meldet, welche Slide gerade im Rail steht — egal ob
      // per Wischen, Pfeil oder Punkt dorthin gelangt. Eine scroll-Position
      // auszurechnen waere fehleranfaellig, sobald das Modal schmaler wird.
      const io = new IntersectionObserver(
        (entries) => {
          entries
            .filter((entry) => entry.isIntersecting)
            .forEach((entry) => {
              this.index = this.slides.indexOf(entry.target);
              this.syncState();
            });
        },
        { root: this.rail, threshold: 0.6 }
      );
      this.slides.forEach((slide) => io.observe(slide));
      this.cleanups.push(() => io.disconnect());
    }

    bindControls() {
      if (this.prev) this.on(this.prev, 'click', () => this.go(this.index - 1));
      if (this.next) this.on(this.next, 'click', () => this.go(this.index + 1));
      this.dots.forEach((dot, i) => this.on(dot, 'click', () => this.go(i)));
    }

    bindAutoplay() {
      // „reduzierte Bewegung" schaltet Autoplay komplett ab — ein Karussell,
      // das von selbst laeuft, ist genau das, was diese Einstellung meint.
      const calm = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (!this.hasAttribute('data-autoplay') || calm.matches) return;

      this.speed = (parseFloat(this.dataset.speed) || 6) * 1000;

      // Der Takt haengt am `open`-Attribut des Modals: geschlossen laeuft
      // nichts. Ein IntersectionObserver taugt dafuer nicht — ein Modal mit
      // visibility:hidden gilt ihm weiterhin als sichtbar.
      const modal = this.closest('modal-dialog');
      if (modal) {
        const mo = new MutationObserver(() => {
          if (modal.hasAttribute('open')) this.restart();
          else this.stop();
        });
        mo.observe(modal, { attributes: true, attributeFilter: ['open'] });
        this.cleanups.push(() => mo.disconnect());
        if (modal.hasAttribute('open')) this.restart();
      } else {
        this.restart();
      }

      // Pausieren statt abbrechen: wer liest oder bedient, soll nicht
      // weitergeschoben werden, danach laeuft es weiter.
      this.on(this, 'mouseenter', () => this.stop());
      this.on(this, 'mouseleave', () => this.restart());
      this.on(this, 'focusin', () => this.stop());
      this.on(this, 'focusout', () => this.restart());
      this.on(this.rail, 'pointerdown', () => this.restart());
      this.cleanups.push(() => this.stop());
    }

    restart() {
      this.stop();
      if (!this.speed) return;
      this.timer = setInterval(() => this.go(this.index + 1), this.speed);
    }

    stop() {
      if (this.timer) clearInterval(this.timer);
      this.timer = null;
    }

    go(target) {
      const last = this.slides.length - 1;
      let i = target;
      if (i < 0) i = this.loop ? last : 0;
      if (i > last) i = this.loop ? 0 : last;

      // Position beim Klick aus offsetLeft rechnen, nie beim Init messen:
      // solange `.page-is-not-idle .popup-modal { display: none }` greift
      // (component-popup.css:18-20), waeren alle Breiten 0. Genau daran
      // scheitert Ignites initPages() (global.js:2004-2020).
      const offset = this.slides[i].offsetLeft - this.slides[0].offsetLeft;
      this.rail.scrollTo({ left: offset });
      this.index = i;
      this.syncState();
    }

    get loop() {
      return this.dataset.loop !== 'false';
    }

    syncState() {
      this.dots.forEach((dot, i) => {
        const active = i === this.index;
        dot.classList.toggle('bw-iv-pops__dot--active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });

      // Ohne Endlos-Schleife sind die Pfeile an den Enden wirkungslos — das
      // gehoert sichtbar gemacht, nicht nur stillschweigend abgefangen.
      if (this.prev && !this.loop) this.prev.disabled = this.index === 0;
      if (this.next && !this.loop) this.next.disabled = this.index === this.slides.length - 1;
    }
  }

  customElements.define('bw-iv-popup-slides', BwIvPopupSlides);
}

/* Zweit-Ausloeser: jeder Link im Dokument, dessen Ziel auf ein Popup dieser
 * Bauart zeigt (href="#BwPop-…"), oeffnet es. Damit funktioniert der Aufruf
 * auch aus einem Rich-Text-Block oder einem Menueintrag. Muster von Ignites
 * custom-popup (global.js:1202-1210); ein einziger delegierter Listener,
 * damit spaeter eingefuegte Links (Section-Reload im Editor) mitgenommen
 * werden. `capture`, damit wir vor dem Sprung ans Seitenende sind.
 */
if (!window.bwIvPopupSlidesAnchorBound) {
  window.bwIvPopupSlidesAnchorBound = true;

  document.addEventListener(
    'click',
    (event) => {
      const link = event.target.closest('a[href*="#BwPop-"]');
      if (!link) return;

      const id = link.hash ? link.hash.slice(1) : '';
      if (!id.startsWith('BwPop-')) return;

      // Nur reagieren, wenn das Popup auch existiert — sonst bleibt der Link
      // ein gewoehnlicher Anker und darf sich normal verhalten.
      const modal = document.getElementById(id);
      if (!modal || typeof modal.show !== 'function') return;

      event.preventDefault();
      modal.show(link);
    },
    true
  );
}

/* Theme-Editor: beim Section-Reload rendert Shopify das Modal neu IN die
 * Section — die beim ersten Oeffnen nach <body> verschobene Kopie bleibt aber
 * liegen. ModalOpener nimmt bei mehreren Treffern den LETZTEN (global.js:1242),
 * und das ist die veraltete Kopie im Body. Ausserdem leert Ignite
 * window.movedModals nie (global.js:888-898), das Modal wuerde also nicht noch
 * einmal verschoben. Beides raeumen wir hier auf. Nur im Editor relevant.
 */
if (window.Shopify && window.Shopify.designMode && !window.bwIvPopupSlidesEditorBound) {
  window.bwIvPopupSlidesEditorBound = true;

  document.addEventListener('shopify:section:load', (event) => {
    const fresh = event.target.querySelector('modal-dialog[id^="BwPop-"]');
    if (!fresh) return;

    document.querySelectorAll('body > modal-dialog[id^="BwPop-"]').forEach((stale) => {
      if (stale.id === fresh.id) stale.remove();
    });

    if (Array.isArray(window.movedModals)) {
      window.movedModals = window.movedModals.filter((id) => id !== fresh.id);
    }
  });
}
